import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { Container, Nav, Navbar, Button } from 'react-bootstrap';
import SiteList from './components/SiteList';
import SiteDetails from './components/SiteDetails';
import FileBrowser from './components/FileBrowser';
import StagingDetails from './components/StagingDetails';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import CustomerManagement from './components/CustomerManagement';
import 'bootstrap/dist/css/bootstrap.min.css';
import axios from 'axios';

function App() {
  const [user, setUser] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios.get('/api/auth/me').then(({ data }) => { if (mounted) setUser(data.user); })
      .catch(() => { if (mounted) setUser(null); })
      .finally(() => { if (mounted) setCheckingSession(false); });
    const interceptor = axios.interceptors.response.use(response => response, error => {
      if (error.response?.status === 401 && mounted) setUser(null);
      return Promise.reject(error);
    });
    return () => { mounted = false; axios.interceptors.response.eject(interceptor); };
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  if (checkingSession) return <Container className="mt-4">Checking your session…</Container>;

  const handleLogout = async () => {
    await axios.post('/api/auth/logout');
    setUser(null);
    localStorage.removeItem('user');
  };

  return (
    <Router>
      <Navbar bg="dark" variant="dark" expand="lg">
        <Container>
          <Navbar.Brand as={Link} to="/">Site Manager</Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="me-auto">
              {user && <Nav.Link as={Link} to="/">Sites</Nav.Link>}
              {user?.tenancy_enabled && user.role === 'admin' && <Nav.Link as={Link} to="/customers">Customers</Nav.Link>}
            </Nav>
            <Nav>
              {user ? (
                <div className="d-flex align-items-center">
                  <span className="text-light me-3">Welcome, {user.username}</span>
                  <Button variant="outline-light" onClick={handleLogout}>Logout</Button>
                </div>
              ) : (
                <Nav.Link as={Link} to="/login">Login</Nav.Link>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
      
      <Container className="mt-4">
        <Routes>
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          
          <Route path="/" element={
            <ProtectedRoute isAuthenticated={!!user}>
              <SiteList tenancyEnabled={!!user?.tenancy_enabled} />
            </ProtectedRoute>
          } />
          <Route path="/customers" element={
            <ProtectedRoute isAuthenticated={!!user && user.role === 'admin' && !!user.tenancy_enabled}>
              <CustomerManagement />
            </ProtectedRoute>
          } />
          
          <Route path="/sites/:siteId" element={
            <ProtectedRoute isAuthenticated={!!user}>
              <SiteDetails tenancyEnabled={!!user?.tenancy_enabled} />
            </ProtectedRoute>
          } />
          
          <Route path="/sites/:siteId/files/*" element={
            <ProtectedRoute isAuthenticated={!!user}>
              <FileBrowser />
            </ProtectedRoute>
          } />
          
          <Route path="/sites/:siteId/staging" element={
            <ProtectedRoute isAuthenticated={!!user}><StagingDetails /></ProtectedRoute>
          } />
          <Route path="/sites/:siteId/staging/files/*" element={
            <ProtectedRoute isAuthenticated={!!user}><FileBrowser staging /></ProtectedRoute>
          } />
          {/* Redirect to login for any unmatched routes */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Container>
    </Router>
  );
}

export default App;
